import { tv } from "tailwind-variants";

const segmentedControlVariants = tv({
  slots: {
    group: "",
    option: [
      "cursor-pointer font-medium whitespace-nowrap",
      "transition-colors duration-150",
      "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none",
    ],
  },
  variants: {
    /*
      `pills` is the original standalone-buttons look. `inset` packs the same
      choices into one track, for places where the control sits beside content
      rather than heading it.
    */
    variant: {
      pills: {
        group: "flex flex-wrap gap-2",
        option: "rounded-full border px-4 py-1.5 text-sm",
      },
      /*
        The track needs its own surface and border: `surface-sunken` is the same
        hex as `canvas` in light mode, so a sunken fill alone is invisible here.
      */
      inset: {
        group: "inline-flex items-center gap-0.5 rounded-full border border-line bg-surface p-0.5",
        option: "rounded-full px-3 py-1 text-xs",
      },
    },
    isSelected: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      variant: "pills",
      isSelected: true,
      class: { option: "border-accent bg-accent text-content-on-accent" },
    },
    {
      variant: "pills",
      isSelected: false,
      class: {
        option:
          "border-line-control bg-surface text-content-muted hover:bg-surface-hover hover:text-content",
      },
    },
    {
      variant: "inset",
      isSelected: true,
      class: { option: "bg-accent text-content-on-accent" },
    },
    {
      variant: "inset",
      isSelected: false,
      class: { option: "text-content-muted hover:bg-surface-hover hover:text-content" },
    },
  ],
  defaultVariants: {
    variant: "pills",
  },
});

const { group, option } = segmentedControlVariants();

export type SegmentedControlOption<TValue extends string> = {
  label: string;
  value: TValue;
};

export type SegmentedControlVariant = "pills" | "inset";

type SegmentedControlProps<TValue extends string> = {
  /** Names the set of choices for assistive tech. */
  label: string;
  options: SegmentedControlOption<TValue>[];
  value: TValue;
  onChange: (value: TValue) => void;
  variant?: SegmentedControlVariant;
  className?: string;
};

/*
  Pill filters shared by the PROMIS screens, which carried two copies with
  different classes — one of them missing `cursor-pointer` and neither exposing
  which pill was active to a screen reader.
*/
export const SegmentedControl = <TValue extends string>({
  className,
  label,
  onChange,
  options,
  value,
  variant = "pills",
}: SegmentedControlProps<TValue>) => {
  return (
    <div aria-label={label} className={group({ className, variant })} role="group">
      {options.map((item) => {
        const isSelected = item.value === value;

        return (
          <button
            aria-pressed={isSelected}
            className={option({ isSelected, variant })}
            key={item.value}
            onClick={() => {
              return onChange(item.value);
            }}
            type="button"
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
};
